import { useMemo } from 'react';
import useCheckoutState from './useCheckoutState';

/**
 * Resolve the effective price amount for the current checkout.
 *
 * Resolution order:
 * 1. dynamicPrice (URL override: per-price `?<reference>_price=`, or the legacy
 *    global `?price=`) — always wins when present. Resolved in useCheckoutState.
 * 2. product.prices — the selectedPriceId entry (the merchant default is seeded
 *    into selectedPriceId at init by useCheckoutState), else the lone price on a
 *    single-price product, else 0. No runtime default fallback: a cleared
 *    selection (e.g. switching to a tab without the selected price) must resolve
 *    to no price, not silently resurrect the default.
 * 3. Legacy fallback — product.price (pre-multi-price products)
 *
 * Returns a plain number (in the product's minor-currency units) so all
 * downstream callers (useCartCost etc.) continue to work unchanged.
 */
export default function useProductPrice() {
  const { page, product, dynamicPrice, selectedPriceId } = useCheckoutState();

  const productPrice = useMemo(() => {
    if (dynamicPrice) {
      return dynamicPrice;
    }

    const prices = product?.prices;
    if (prices && prices.length > 0) {
      const enabledPrices = prices.filter(p => p.enabled !== false);

      let selectedPrice = null;

      if (selectedPriceId) {
        selectedPrice = enabledPrices.find(p => String(p._id) === String(selectedPriceId));
      }

      if (!selectedPrice && enabledPrices.length === 1) {
        selectedPrice = enabledPrices[0];
      }

      return Number(selectedPrice?.amount || 0);
    }

    // TODO(M14): legacy product.price fallback for products without prices[] — remove after the backfill migration
    return Number(product?.price || 0);
  }, [product, dynamicPrice, selectedPriceId]);

  if (page.type === 'event') {
    return 0;
  }

  if (!product) {
    return 0;
  }

  return productPrice;
}
